import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useChat } from '../contexts/ChatContext';
import { toast } from 'react-toastify';
import ConversationList from '../components/chat/ConversationList';
import MessageList from '../components/chat/MessageList';
import MessageInput from '../components/chat/MessageInput';
import UserSelector from '../components/chat/UserSelector';

const StudentChatPage = () => {
  const { user } = useAuth();
  const {
    conversations,
    activeConversation,
    setActiveConversation,
    messages,
    contacts,
    isConnected,
    typingUsers,
    sendMessage,
    sendTyping,
    markAsRead,
    createConversation,
    uploadFile,
    fetchMessages,
    fetchConversations
  } = useChat();

  const [showUserSelector, setShowUserSelector] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  useEffect(() => {
    if (activeConversation) {
      fetchMessages(activeConversation.id);
      markAsRead(activeConversation.id);
    }
  }, [activeConversation, fetchMessages, markAsRead]);

  const handleSelectConversation = (conversation) => {
    setActiveConversation(conversation);
  };

  const handleSendMessage = (content) => {
    if (activeConversation) {
      sendMessage(activeConversation.id, content, 'text', null);
    }
  };

  const handleTyping = () => {
    if (activeConversation) {
      sendTyping(activeConversation.id);
    }
  };

  const handleFileUpload = async (file) => {
    if (!activeConversation) return;

    try {
      const result = await uploadFile(file);
      if (result?.file_url) {
        sendMessage(activeConversation.id, `Archivo: ${file.name}`, 'file', result.file_url);
        // No mostrar notificación - el usuario ya sabe que envió el archivo
      }
    } catch (error) {
      toast.error('Error al enviar el archivo');
    }
  };

  const handleSelectUser = async (user) => {
    setLoading(true);
    try {
      const result = await createConversation(user.id, user.type || 'student');
      if (result?.conversation) {
        // Transformar la conversación para que tenga el formato esperado
        const formattedConversation = {
          id: result.conversation.id,
          participant_name: result.conversation.participant_name,
          participant_email: result.conversation.participant_email,
          participant_avatar_url: result.conversation.participant_avatar_url,
          participant_avatar_color: result.conversation.participant_avatar_color || '#6366f1',
          participant_type: result.conversation.participant_type,
          last_message: result.conversation.last_message,
          last_message_at: result.conversation.last_message_at,
          unread_count: result.conversation.unread_count || 0
        };
        
        setActiveConversation(formattedConversation);
        // No mostrar notificación - es obvio que la conversación se inició
      }
    } catch (error) {
      console.error('Error creating conversation:', error);
      toast.error('Error al crear la conversación');
    } finally {
      setLoading(false);
    }
  };

  const currentMessages = activeConversation ? messages[activeConversation.id] || [] : [];
  const typingUser = activeConversation ? typingUsers[activeConversation.id] : null;

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <div className="bg-gray-800 shadow-lg border-b border-gray-700 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => window.location.href = '/app'}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="Volver al Dashboard"
              >
                <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <svg className="w-8 h-8 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <div>
                <h1 className="text-xl font-bold text-gray-100">Mensajes</h1>
                <p className="text-sm text-gray-400">
                  {isConnected ? (
                    <span className="flex items-center">
                      <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                      Conectado
                    </span>
                  ) : (
                    <span className="flex items-center">
                      <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                      Desconectado
                    </span>
                  )}
                </p>
              </div>
            </div>
            
            <button
              onClick={() => setShowUserSelector(true)}
              className="px-4 py-2 bg-gradient-to-r from-cyan-600 to-teal-600 text-white rounded-lg hover:from-cyan-700 hover:to-teal-700 transition-colors flex items-center space-x-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span>Nueva conversación</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="bg-gray-800 rounded-lg shadow-2xl overflow-hidden border border-gray-700" style={{ height: 'calc(100vh - 180px)' }}>
          <div className="flex h-full">
            {/* Conversations sidebar */}
            <div className="w-80 border-r border-gray-700 bg-gray-900 flex flex-col">
              {/* Sidebar header */}
              <div className="p-4 border-b border-gray-700">
                <h2 className="text-lg font-semibold text-gray-100">Conversaciones</h2>
                <p className="text-sm text-gray-400 mt-1">
                  {conversations.length} conversación{conversations.length !== 1 ? 'es' : ''}
                </p>
              </div>

              {/* Conversation list */}
              <ConversationList
                conversations={conversations}
                activeConversationId={activeConversation?.id}
                onSelectConversation={handleSelectConversation}
                currentUserId={user?.id}
              />
            </div>

            {/* Chat area */}
            <div className="flex-1 flex flex-col">
              {activeConversation ? (
                <>
                  {/* Chat header */}
                  <div className="px-6 py-4 border-b border-gray-700 bg-gray-800">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div
                          className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold"
                          style={{ backgroundColor: activeConversation.participant_avatar_color || '#6366f1' }}
                        >
                          {activeConversation.participant_avatar_url ? (
                            <img
                              src={activeConversation.participant_avatar_url}
                              alt={activeConversation.participant_name}
                              className="w-full h-full rounded-full object-cover"
                            />
                          ) : (
                            <span>{activeConversation.participant_name?.charAt(0).toUpperCase()}</span>
                          )}
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-100">
                            {activeConversation.participant_name}
                          </h3>
                          {activeConversation.participant_type && (
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              activeConversation.participant_type === 'mentor' 
                                ? 'bg-purple-900/50 text-purple-300'
                                : 'bg-cyan-900/50 text-cyan-300'
                            }`}>
                              {activeConversation.participant_type === 'mentor' ? '👨‍🏫 Mentor' : '👤 Estudiante'}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Messages */}
                  <MessageList
                    messages={currentMessages}
                    currentUserId={user?.id}
                    typingUser={typingUser}
                  />

                  {/* Message input */}
                  <MessageInput
                    onSend={handleSendMessage}
                    onTyping={handleTyping}
                    onFileUpload={handleFileUpload}
                    disabled={!isConnected}
                  />
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-gray-400">
                  <div className="text-center">
                    <svg className="w-24 h-24 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Selecciona una conversación</h3>
                    <p className="text-sm">Elige una conversación de la izquierda o inicia una nueva</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* User selector modal */}
      {showUserSelector && (
        <UserSelector
          contacts={contacts}
          onSelectUser={handleSelectUser}
          onClose={() => setShowUserSelector(false)}
        />
      )}

      {/* Loading overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Creando conversación...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentChatPage;
